import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../lib/apiError';
import type { IndexQueryState, PagedResult } from './types';

const DEFAULT_PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The resting status filter: active records only.
 *
 * Nothing is ever hard-deleted here, so without this every list would grow forever with
 * rows the user deleted on purpose. "Show deleted" is what lifts it.
 */
const ACTIVE_ONLY: Record<string, string[]> = { status: ['active'] };

/**
 * The query lives in the URL, so a REFRESH lands the reader back where they were.
 *
 * Product call, 28 Sep 2026. Without it F5 reset every list to its first tab with no filters —
 * which on a page whose folder nav is a CLOSED taxonomy (Stock Levels) means the reader is put
 * on Consumables however long they had been working on Equipment. The address bar is also the
 * honest place for it: a filtered list the reader can SEE is filtered, and can send to someone.
 *
 * <b>This is not the persistence the product refuses.</b> "Show deleted is not persisted" is a
 * rule about state outliving the VISIT — a remembered record filter is how a deleted row comes
 * to be treated as live. A URL is the current page’s address: it dies with the tab, it is
 * visible, and a fresh link carries none of it.
 *
 * <b>A facet PRESENT AND EMPTY is not an absent one</b>, and the codec has to keep them apart —
 * `f_status=` is "ticked to nothing" and matches no row, where no `f_status` at all leaves the
 * axis unmentioned. That is the three-state facet contract, and a `split(',')` over an empty
 * string returns `['']` rather than `[]`, which would send one garbage value instead.
 */
const FACET_PREFIX = 'f_';

interface UrlQuery {
  search: string;
  sort: string | null;
  page: number;
  facets: Record<string, string[]> | null;
}

/** Reads the query out of `window.location`. `facets: null` means the URL named none. */
function readUrlQuery(): UrlQuery {
  const empty: UrlQuery = { search: '', sort: null, page: 1, facets: null };

  if (typeof window === 'undefined') return empty;

  try {
    const params = new URLSearchParams(window.location.search);
    const facets: Record<string, string[]> = {};
    let named = false;

    params.forEach((value, key) => {
      if (!key.startsWith(FACET_PREFIX)) return;

      named = true;
      facets[key.slice(FACET_PREFIX.length)] = value === '' ? [] : value.split(',');
    });

    const page = Number.parseInt(params.get('page') ?? '', 10);

    return {
      search: params.get('q') ?? '',
      sort: params.get('sort'),
      page: Number.isFinite(page) && page > 0 ? page : 1,
      facets: named ? facets : null,
    };
  } catch {
    // A malformed address is not a reason for the list not to render.
    return empty;
  }
}

/**
 * Writes the query back, through `replaceState` rather than a navigation.
 *
 * `pushState` would put every keystroke of a debounced search in the history, so Back would
 * walk the reader letter by letter out of a filter instead of off the page.
 */
function writeUrlQuery(query: { search: string; sort: string | null; page: number; facets: Record<string, string[]> }) {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams();

    if (query.search !== '') params.set('q', query.search);
    if (query.sort !== null) params.set('sort', query.sort);
    if (query.page > 1) params.set('page', String(query.page));

    for (const [key, values] of Object.entries(query.facets)) {
      params.set(`${FACET_PREFIX}${key}`, values.join(','));
    }

    const search = params.toString();

    window.history.replaceState(window.history.state, '', search === '' ? window.location.pathname : `${window.location.pathname}?${search}`);
  } catch {
    // Storage or history blocked. The list still works; it just does not survive a refresh.
  }
}

/**
 * Owns list state and fetching for `<IndexPage>` — reference §3.
 *
 * Server-side everything: data, facet counts, search, pagination, and sort all come from
 * the one list endpoint. The client filters nothing.
 */
export function useIndexQuery<T>(
  module: string,
  fetcher: (query: IndexQueryState, signal: AbortSignal) => Promise<PagedResult<T>>,
  reloadKey = 0,
  /**
   * Serve a query this hook has ALREADY answered from memory, then revalidate behind it.
   *
   * Opt-in, and off for every page but one. It exists for a list whose folder tabs are its
   * primary filing — Stock Levels — where moving between kinds otherwise blanks the table to
   * a skeleton and refetches a page the reader looked at ten seconds ago.
   *
   * **The key is the WHOLE query, not the tab**: search, sort, page and every facet. Keyed on
   * the tab alone the cache would answer a search with the rows from before it, which is the
   * "filtering the page already in memory" bug wearing a different hat.
   *
   * It is stale-while-revalidate, so a cached answer is always followed by a real one, and any
   * write clears it outright through `reloadKey`. Nothing is persisted — a cache that outlived
   * the visit is how a deleted row gets treated as live.
   */
  cacheAnsweredQueries = false,
  /**
   * Facets the list ARRIVES on, merged over the resting active-only filter.
   *
   * It exists for `navSlices`, which is a CLOSED taxonomy with no All tab — so the list has
   * to land already standing on a slice rather than unconstrained. Seeded once, in the state
   * initialiser: it is where the reader starts, not a filter the shell keeps re-applying.
   */
  initialFacets?: Record<string, string[]>,
) {
  /*
    SEEDED FROM THE ADDRESS, once. `initialFacets` is the slice the page ARRIVES on and the
    URL is where the reader actually was, so the URL wins where it names anything at all —
    otherwise a refresh on Equipment would land back on the first slice with the address bar
    still saying Equipment.
  */
  const [urlSeed] = useState(readUrlQuery);

  const [searchInput, setSearchInput] = useState(urlSeed.search);
  const [search, setSearch] = useState(urlSeed.search);
  const [page, setPage] = useState(urlSeed.page);
  const [sort, setSort] = useState<string | null>(urlSeed.sort);
  const [facets, setFacets] = useState<Record<string, string[]>>(
    () => urlSeed.facets ?? { ...ACTIVE_ONLY, ...initialFacets },
  );

  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [facetCounts, setFacetCounts] = useState<PagedResult<T>['facets']>({});
  const [loading, setLoading] = useState(true);

  /*
    A NEWER COPY IS COMING, and the rows on screen are still the answer to this question.

    The same split `<ProfileShell>` draws between a first load and a refresh, one shell along:
    `loading` means there is nothing to show yet, `refreshing` means there is. A write bumps
    `reloadKey`, which clears the cache — so before this the page blanked to a skeleton on
    every save, throwing away the tab, the scroll and everything the reader was looking at in
    order to report progress on one row of it.

    A QUERY CHANGE still skeletons, deliberately: the rows on screen answer a different
    question, and the skeleton is what makes a tab switch read as a page load.
  */
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Debounced search — 300ms, matching every other list in the product.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const query = useMemo<IndexQueryState>(
    () => ({ search, page, pageSize: DEFAULT_PAGE_SIZE, sort, facets }),
    [search, page, sort, facets],
  );

  /*
    THE ADDRESS FOLLOWS THE QUERY, so a refresh lands the reader back on the tab and the
    filters they were working in. `pageSize` is deliberately left out — it is the shell’s
    constant rather than a choice the reader made, and a number in the address that nobody
    set is one somebody will edit.
  */
  useEffect(() => {
    writeUrlQuery({ search, sort, page, facets });
  }, [search, sort, page, facets]);

  /*
    Derived from the status filter rather than held alongside it. The rail's Status facet
    and this toggle are two controls over one axis; storing a second flag would let them
    contradict each other, and then the list would agree with only one of them.

    Deliberately not persisted. A view that quietly kept showing deleted rows from a
    previous session is how a deleted record gets treated as live.
  */
  const showDeleted = !(facets.status?.length === 1 && facets.status[0] === 'active');

  const setShowDeleted = useCallback((next: boolean) => {
    setFacets((current) => {
      const updated = { ...current };

      if (next) {
        // Drop the constraint entirely: deleted rows become eligible, and the rail's Status
        // facet keeps working as the finer control within them.
        delete updated.status;
      } else {
        updated.status = ['active'];
      }

      return updated;
    });

    setPage(1);
  }, []);

  const abortRef = useRef<AbortController | null>(null);

  /*
    What the rows on screen ANSWER, and whether there are any — the pair that separates a
    refresh from a first load. Refs rather than state: nothing renders off them, and reading
    them out of the fetch callback is what keeps `items` out of its dependency list.
  */
  const answeredRef = useRef<string | null>(null);
  const hasRowsRef = useRef(false);

  /*
    Cleared whenever `reloadKey` moves, which is what every write on every page bumps. A cache
    that survived a create would show the reader a list their own record is missing from.
  */
  const cacheRef = useRef(new Map<string, PagedResult<T>>());

  useEffect(() => {
    cacheRef.current.clear();
  }, [reloadKey]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    /*
      A query already answered is served from memory and NOT re-announced as loading — the
      skeleton is what makes a tab switch feel like a page load, and there is nothing to wait
      for when the rows are already in hand. The request still goes out underneath.
    */
    const key = cacheAnsweredQueries ? JSON.stringify(query) : null;
    const cached = key === null ? undefined : cacheRef.current.get(key);

    if (cached) {
      setItems(cached.items);
      setTotal(cached.total);
      setFacetCounts(cached.facets);
    }

    /*
      IS THIS THE SAME QUESTION the rows on screen already answer?

      Read off a ref rather than off `items`, which would put the item list in this callback’s
      dependencies and rebuild it — and therefore refire the effect below — on every response.
      A refetch that retriggers itself is the one shape this must not take.
    */
    const signature = JSON.stringify(query);
    const sameQuestion = answeredRef.current === signature && hasRowsRef.current;

    setLoading(!cached && !sameQuestion);
    setRefreshing(!cached && sameQuestion);
    setError(null);

    try {
      const result = await fetcher(query, controller.signal);

      if (key !== null) {
        cacheRef.current.set(key, result);
      }

      setItems(result.items);
      setTotal(result.total);
      setFacetCounts(result.facets);

      answeredRef.current = JSON.stringify(query);
      hasRowsRef.current = result.items.length > 0;
    } catch (caught) {
      if (controller.signal.aborted) {
        return;
      }

      setError(caught instanceof ApiError ? caught : new ApiError('Could not load this list.', 0));
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [fetcher, query, cacheAnsweredQueries]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load, reloadKey]);

  /*
    Set a facet outright, rather than flipping one value of it.

    The status folder tabs are single-choice over an axis the rail exposes as checkboxes, so
    they cannot be expressed as a sequence of toggles — picking a tab has to REPLACE whatever
    was ticked. Both controls write this one piece of state, which is what stops the strip and
    the rail from disagreeing about a list that can only obey one of them.
  */
  const selectFacet = useCallback((key: string, values: string[]) => {
    setFacets((current) => {
      const present = key in current;
      const selected = current[key] ?? [];

      /*
        The same selection returns the same object. A fresh one would re-run the query for
        a selection that did not change — clicking the status tab you are already on.

        Presence is part of the comparison: an ABSENT key and an empty array are both zero
        values but different filters, so bailing out on length alone made `selectFacet(key,
        [])` a no-op on an unconstrained axis — which is exactly the gesture that clears it.
      */
      if (present && selected.length === values.length && selected.every((v, i) => v === values[i])) {
        return current;
      }

      /*
        An empty array is KEPT, not deleted. It is the reader unticking every value on an
        axis, which means no row qualifies — a different filter from never having mentioned
        the axis, which is what deleting the key expresses. Collapsing the two is what made
        unticking everything silently show the whole table. Use clearFacet for the other one.
      */
      return { ...current, [key]: values };
    });

    setPage(1);
  }, []);

  /**
    Drop the axis entirely — the reader has no opinion about it, so every row qualifies.

    The counterpart to selectFacet(key, []), which says the opposite: they DO have an
    opinion and it excludes everything. The two look identical on the wire unless the
    parameter's presence carries the difference, which is why this deletes the key and that
    one keeps it.
  */
  const clearFacet = useCallback((key: string) => {
    setFacets((current) => {
      if (!(key in current)) {
        return current;
      }

      const updated = { ...current };
      delete updated[key];
      return updated;
    });

    setPage(1);
  }, []);

  /** Cycles a column: ascending -> descending -> unsorted. */
  const toggleSort = useCallback((field: string) => {
    setSort((current) => {
      if (current === `${field}:asc`) return `${field}:desc`;
      if (current === `${field}:desc`) return null;
      return `${field}:asc`;
    });

    setPage(1);
  }, []);

  const activeFilterCount = Object.entries(facets).reduce((sum, [key, values]) => {
    // "Active only" is the default, so counting it would put a badge on the toolbar's filter
    // button for a list nobody has filtered.
    if (key === 'status' && values.length === 1 && values[0] === 'active') {
      return sum;
    }

    // An axis narrowed to nothing is very much a filter — the most restrictive one there
    // is — so it counts as one rather than as its zero values.
    return sum + (values.length === 0 ? 1 : values.length);
  }, 0);
  const pageCount = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const lastRow = Math.min(page * DEFAULT_PAGE_SIZE, total);

  return {
    module,
    showDeleted,
    setShowDeleted,
    searchInput,
    setSearchInput,
    search,
    page,
    setPage,
    pageCount,
    pageSize: DEFAULT_PAGE_SIZE,
    firstRow,
    lastRow,
    sort,
    toggleSort,
    facets,
    facetCounts,
    selectFacet,
    clearFacet,
    activeFilterCount,
    items,
    total,
    loading,
    refreshing,
    error,
    reload: load,
  };
}

export type IndexQuery<T> = ReturnType<typeof useIndexQuery<T>>;
